import { CommonModule, NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { LanguageService } from '@shared/services/language.service';
import { Auction } from 'src/app/articles-manager/interfaces/auction.interface';
import { PagesService } from 'src/app/pages-manager/services/pages.service';

@UntilDestroy()
@Component({
  selector: 'app-article-description',
  templateUrl: './article-description.component.html',
  styleUrls: ['./article-description.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [TranslatePipe, NgClass, FaIconComponent, CommonModule],
})
export class ArticleDescriptionComponent implements AfterViewInit, OnDestroy {
  isEventNow = input(false);
  selectedAuction = input<Auction>();
  isCertificatePage = input(false);
  descriptionClicked = signal(false);
  containerHeight = signal<string>('auto');

  // minimal swipe state
  private touchStartX: number | null = null;
  private touchStartY: number | null = null;
  private readonly swipeThreshold = 40; // px
  private readonly verticalToleranceRatio = 0.5;

  // Refs for adaptive height
  @ViewChild('sliderContainer', { static: false })
  sliderContainerRef?: ElementRef<HTMLElement>;
  @ViewChild('sliderTrack', { static: false })
  sliderTrackRef?: ElementRef<HTMLElement>;
  @ViewChild('descriptionSlide', { static: false })
  descriptionSlideRef?: ElementRef<HTMLElement>;
  @ViewChild('disclaimerSlide', { static: false })
  disclaimerSlideRef?: ElementRef<HTMLElement>;

  private resizeObserver?: ResizeObserver;

  hasDisclaimer = computed(
    () => !!this.notes() && !this.isCertificatePage() && !this.isEventNow()
  );
  activeIndex = computed(() => (this.descriptionClicked() ? 1 : 0));
  totalSlides = computed(() => (this.hasDisclaimer() ? 2 : 1));
  tabIndicatorTransform = computed(
    () => `translateX(${this.activeIndex() * 100}%)`
  );

  // Horizontal track sliding (reintroduced animation)
  trackWidth = computed(() => `${this.totalSlides() * 100}%`);
  slideBasis = computed(() => `${100 / this.totalSlides()}%`);
  trackTransform = computed(() => {
    // Each slide occupies (100 / totalSlides)% of track width
    const shift = (100 / this.totalSlides()) * this.activeIndex();
    return `translateX(-${shift}%)`;
  });

  description = computed(() => {
    return (
      this.selectedAuction()?.article?.articleInfoList[0]?.description ??
      this.selectedAuction()?.article?.articleDescription
    );
  });
  notes = computed(() => {
    return this.selectedAuction()?.article?.articleInfoList[0]?.notes;
  });

  isMemorabid = computed(() => this.selectedAuction()?.idAuctionType === 1);

  constructor() {
    // Recalculate height when activeIndex changes
    effect(
      () => {
        // read to set dependency
        this.activeIndex();
        // defer measurement to next microtask so DOM updates (transform) are applied
        queueMicrotask(() => this.updateContainerHeight());
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  ngAfterViewInit(): void {
    // SSR guard
    if (typeof window === 'undefined') {
      return;
    }
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() =>
        this.updateContainerHeight()
      );
      if (this.descriptionSlideRef?.nativeElement)
        this.resizeObserver.observe(this.descriptionSlideRef.nativeElement);
      if (this.disclaimerSlideRef?.nativeElement)
        this.resizeObserver.observe(this.disclaimerSlideRef.nativeElement);
    }
    this.updateContainerHeight();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  onDescription() {
    this.descriptionClicked.set(false);
    this.updateTransform();
  }

  onDisclaimer() {
    this.descriptionClicked.set(true);
    this.updateTransform();
  }

  onTouchStart(ev: TouchEvent) {
    if (ev.touches.length !== 1) return;
    const t = ev.touches[0];
    this.touchStartX = t.clientX;
    this.touchStartY = t.clientY;
  }

  onTouchEnd(ev: TouchEvent) {
    if (this.touchStartX === null || this.touchStartY === null) return;
    if (ev.changedTouches.length !== 1) {
      this.resetSwipe();
      return;
    }
    const t = ev.changedTouches[0];
    const dx = t.clientX - this.touchStartX;
    const dy = t.clientY - this.touchStartY;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    if (
      absDx >= this.swipeThreshold &&
      absDx >= absDy * this.verticalToleranceRatio
    ) {
      if (dx < 0 && !this.descriptionClicked() && this.hasDisclaimer()) {
        this.onDisclaimer();
      } else if (dx > 0 && this.descriptionClicked()) {
        this.onDescription();
      }
    }
    this.resetSwipe();
  }

  private resetSwipe() {
    this.touchStartX = this.touchStartY = null;
  }

  // Attach to (touchstart)
  private updateTransform() {
    // Recalculate height & allow transform animation to run via CSS
    this.updateContainerHeight();
  }

  private heightResetTimeout: number | undefined;
  private updateContainerHeight() {
    const active =
      this.activeIndex() === 0
        ? this.descriptionSlideRef?.nativeElement
        : this.disclaimerSlideRef?.nativeElement;
    const containerEl = this.sliderContainerRef?.nativeElement;
    if (!active || !containerEl) return;
    if (typeof window === 'undefined') return; // SSR safety
    const target = active.scrollHeight;
    const current = containerEl.offsetHeight;
    if (current === target && this.containerHeight() === 'auto') return;
    if (this.heightResetTimeout) clearTimeout(this.heightResetTimeout);
    if (this.containerHeight() === 'auto') {
      this.containerHeight.set(current + 'px');
    }
    if (typeof requestAnimationFrame === 'undefined') {
      this.containerHeight.set(target + 'px');
      return;
    }
    requestAnimationFrame(() => {
      this.containerHeight.set(target + 'px');
      if (this.totalSlides() === 1 && typeof window !== 'undefined') {
        this.heightResetTimeout = window.setTimeout(() => {
          if (containerEl.offsetHeight === target) {
            this.containerHeight.set('auto');
          }
        }, 400);
      }
    });
  }
}
